// src/pages/SalesSummaryBreakdown.js
// Redesigned: larger fonts, trend arrows, staff roster display, dynamic layout

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/darkTheme';

const C = {
  blue: '#2563EB', blueDark: '#1E40AF', teal: '#06B6D4', red: '#E4002B',
  gray50: '#F8FAFC', gray100: '#F1F5F9', gray200: '#E2E8F0',
  gray400: '#94A3B8', gray600: '#475569', gray800: '#1E293B', gray900: '#0F172A',
  white: '#FFFFFF', success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
};

const fmt  = (v) => (v === null || v === undefined || isNaN(v)) ? '—' : Math.round(Number(v)).toLocaleString();
const fmtPct = (v) => (v === null || v === undefined || isNaN(v)) ? '—' : `${Math.round(v * 100)}%`;

function gapColor(v, isDark = false) { if (v == null) return isDark ? '#64748B' : C.gray400; return v >= 0 ? (isDark ? '#34D399' : C.success) : (isDark ? '#F87171' : C.danger); }
function pctColor(v, isDark = false) { if (v == null) return isDark ? '#64748B' : C.gray400; if (v >= 1) return isDark ? '#34D399' : C.success; if (v >= 0.75) return isDark ? '#FBBF24' : C.warning; return isDark ? '#F87171' : C.danger; }

// Trend arrow matching the Excel conditional formatting
function trendArrow(gap, isDark = false) {
  if (gap == null || isNaN(gap)) return { icon: '—', color: isDark ? '#64748B' : C.gray400 };
  if (gap > 0) return { icon: '▲', color: isDark ? '#34D399' : '#059669' };
  if (gap < 0) return { icon: '▼', color: isDark ? '#F87171' : '#DC2626' };
  return { icon: '▶', color: isDark ? '#FBBF24' : '#D97706' };
}

const CATEGORY_COLORS = {
  'Port': '#2563EB', 'SIMS': '#06B6D4', 'Postpaid': '#E4002B', 'Postpaid Accelerator': '#DC2626',
  'D+': '#7C3AED', 'D+ Accelerator': '#6D28D9', 'ACC': '#D97706',
  'Phones': '#059669', 'Phones Accelerator': '#047857', 'Plans': '#DB2777',
};

// A wide table with overflow-x:auto works, but on a touch device there's
// no visible scrollbar the way there is with a mouse -- nothing hints that
// scrolling right reveals more columns. This wraps a scrollable table with
// a soft shadow on whichever edge still has more content past it, so a
// tablet user can see at a glance there's more to scroll to.
export function ScrollableTableWrapper({ children }) {
  const scrollRef = useRef(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const checkShadows = () => {
      setShowLeftShadow(el.scrollLeft > 4);
      setShowRightShadow(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    checkShadows();
    el.addEventListener('scroll', checkShadows);
    window.addEventListener('resize', checkShadows);
    return () => {
      el.removeEventListener('scroll', checkShadows);
      window.removeEventListener('resize', checkShadows);
    };
  }, [children]);

  return (
    <div style={{ position: 'relative' }}>
      {showLeftShadow && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 24, background: 'linear-gradient(90deg, rgba(0,0,0,0.10), transparent)', pointerEvents: 'none', zIndex: 1 }} />}
      {showRightShadow && <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 24, background: 'linear-gradient(270deg, rgba(0,0,0,0.10), transparent)', pointerEvents: 'none', zIndex: 1 }} />}
      <div ref={scrollRef} style={{ overflowX: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

// ── Category breakdown table — redesigned with trend arrows ──
export function CategoryBreakdownTable({ title, subtitle, staffCount, categories, showSplit = false }) {
  const { isDark } = useTheme();
  const T = getThemeColors(isDark);
  const HDRS = ['Category','Target','MTD','MTD Tgt','GAP','','RRR','CRR','Avg/Staff','Est. Close','Est. GAP','','% Est.'];
  const splitHdrs = showSplit ? ['Sup Tgt','Balance','Per Staff'] : [];

  return (
    <div style={{ background: T.cardBg, borderRadius: 14, boxShadow: isDark ? '0 1px 6px rgba(0,0,0,0.3)' : '0 1px 6px rgba(0,0,0,0.05)', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
      {title && (
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textSecondary }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: T.textFaint, marginTop: 1 }}>{subtitle}</div>}
          </div>
          {staffCount != null && (
            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 8, background: T.cardBgAlt, color: T.textMuted, fontWeight: 700 }}>
              👥 {staffCount}
            </span>
          )}
        </div>
      )}
      <ScrollableTableWrapper>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: isDark ? '#0B1220' : C.gray800 }}>
              {[...HDRS, ...splitHdrs].map((h, i) => (
                <th key={i} style={{
                  padding: '10px 12px', textAlign: i === 0 ? 'left' : 'center',
                  fontWeight: 700, color: C.white, fontSize: 12, textTransform: 'uppercase',
                  letterSpacing: '0.3px', whiteSpace: 'nowrap',
                  width: (h === '' ? 28 : undefined),
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, idx) => {
              const gapT = trendArrow(cat.gap, isDark);
              const estGapT = trendArrow(cat.estimatedClosingGap, isDark);
              const isAccel = cat.label.includes('Accelerator');
              const catColor = CATEGORY_COLORS[cat.label] || T.textSecondary;

              return (
                <tr key={cat.label} style={{
                  background: isAccel ? (isDark ? '#1A2333' : '#FAFAFA') : (idx % 2 === 0 ? T.cardBg : T.cardBgAlt),
                  borderBottom: `2px solid ${T.border}`,
                  borderLeft: `4px solid ${catColor}`,
                }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: catColor, whiteSpace: 'nowrap', fontSize: 14 }}>
                    {isAccel ? `⚡ ${cat.label}` : cat.label}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: T.textSecondary }}>{fmt(cat.target)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 800, fontSize: 15, color: T.textPrimary }}>{fmt(cat.mtd)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: T.textMuted, fontSize: 14 }}>{fmt(cat.mtdTarget)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: gapColor(cat.gap, isDark), fontSize: 14 }}>
                    {cat.gap >= 0 ? '+' : ''}{fmt(cat.gap)}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 16, width: 28 }}>
                    <span style={{ color: gapT.color, fontWeight: 900 }}>{gapT.icon}</span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: isDark ? '#FBBF24' : '#B45309', fontWeight: 700, background: isDark ? 'rgba(251,191,36,0.12)' : '#FFFBEB', fontSize: 14 }}>{fmt(cat.rrr)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: T.textSecondary }}>{fmt(cat.crr)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: T.textSecondary }}>{fmt(cat.avgPerStaff)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: 14, color: T.textSecondary }}>{fmt(cat.estimatedClosing)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', color: gapColor(cat.estimatedClosingGap, isDark), fontWeight: 700, fontSize: 14 }}>
                    {cat.estimatedClosingGap >= 0 ? '+' : ''}{fmt(cat.estimatedClosingGap)}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 16, width: 28 }}>
                    <span style={{ color: estGapT.color, fontWeight: 900 }}>{estGapT.icon}</span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 800, fontSize: 14, color: pctColor(cat.pctEstimatedClosing, isDark),
                    background: cat.pctEstimatedClosing >= 1 ? (isDark ? 'rgba(52,211,153,0.12)' : '#ECFDF5') : cat.pctEstimatedClosing >= 0.75 ? (isDark ? 'rgba(251,191,36,0.12)' : '#FFFBEB') : (isDark ? 'rgba(248,113,113,0.12)' : '#FEF2F2') }}>
                    {fmtPct(cat.pctEstimatedClosing)}
                  </td>
                  {showSplit && <>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: T.blue, fontSize: 14 }}>{fmt(cat.supervisorTarget)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontSize: 14, color: T.textSecondary }}>{fmt(cat.staffBalance)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: isDark ? '#A78BFA' : '#7C3AED', fontSize: 14 }}>{fmt(cat.perStaffTarget)}</td>
                  </>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollableTableWrapper>
    </div>
  );
}

// ── Staff roster display (read-only list with role badges) ──
export function StaffRosterDisplay({ roster, storeCode, onSelectStaff }) {
  const { isDark } = useTheme();
  const T = getThemeColors(isDark);
  if (!roster || roster.length === 0) return null;
  const roleStyle = (role) => {
    if (role === 'Supervisor') return { bg: isDark ? 'rgba(167,139,250,0.15)' : '#EDE9FE', color: isDark ? '#A78BFA' : '#6D28D9' };
    if (role === 'Assistant Supervisor') return { bg: isDark ? 'rgba(251,191,36,0.15)' : '#FEF3C7', color: isDark ? '#FBBF24' : '#92400E' };
    return { bg: T.cardBgAlt, color: T.textMuted };
  };

  return (
    <div style={{ background: T.cardBg, borderRadius: 12, border: `1px solid ${T.border}`, padding: '12px 14px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, marginBottom: 8 }}>
        👥 Staff Roster ({roster.length}){onSelectStaff && <span style={{ fontWeight: 500, color: T.textFaint }}> — tap a name for their snapshot</span>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {roster.map(person => {
          const rs = roleStyle(person.role);
          const Tag = onSelectStaff ? 'button' : 'div';
          return (
            <Tag key={person.id}
              onClick={onSelectStaff ? () => onSelectStaff(person) : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px',
                borderRadius: 8, background: T.cardBgAlt, border: `1px solid ${T.divider}`, fontSize: 13,
                cursor: onSelectStaff ? 'pointer' : 'default', fontFamily: 'inherit',
              }}>
              <span style={{ fontWeight: 600, color: T.textSecondary }}>{person.full_name}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 5, background: rs.bg, color: rs.color }}>
                {person.role === 'Assistant Supervisor' ? 'Asst Sup' : person.role}
              </span>
              {onSelectStaff && <span style={{ fontSize: 11 }}>📸</span>}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

// ── Inline roster editor for one store ──
export function RosterEditor({ storeCode, roster, onChanged }) {
  const { isDark } = useTheme();
  const T = getThemeColors(isDark);
  const [adding, setAdding]   = useState(false);
  const [name, setName]       = useState('');
  const [role, setRole]       = useState('Staff');
  const [busy, setBusy]       = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName]   = useState('');
  const [editRole, setEditRole]   = useState('Staff');
  const [showDormant, setShowDormant] = useState(false);
  const [dormantList, setDormantList] = useState([]);

  const loadDormant = async () => {
    try {
      const res = await axios.get(`/api/staff/${storeCode}/dormant`);
      setDormantList(res.data);
    } catch { setDormantList([]); }
  };

  const toggleDormant = async () => {
    if (!showDormant) await loadDormant();
    setShowDormant(d => !d);
  };

  const addStaff = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await axios.post('/api/staff', { store_code: storeCode, full_name: name.trim(), role });
      setName(''); setRole('Staff'); setAdding(false);
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add staff member');
    } finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    setBusy(true);
    try {
      await axios.put(`/api/staff/${id}`, { full_name: editName.trim(), role: editRole });
      setEditingId(null);
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update');
    } finally { setBusy(false); }
  };

  const makeDormant = async (id, personName) => {
    if (!window.confirm(`Make ${personName} dormant? They'll be hidden from active roster but preserved for history.`)) return;
    setBusy(true);
    try {
      await axios.delete(`/api/staff/${id}`);
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const reactivate = async (id) => {
    setBusy(true);
    try {
      await axios.put(`/api/staff/${id}`, { is_active: 1 });
      await loadDormant();
      await onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const roleStyle = (role) => {
    if (role === 'Supervisor') return { bg: isDark ? 'rgba(167,139,250,0.15)' : '#EDE9FE', color: isDark ? '#A78BFA' : '#6D28D9' };
    if (role === 'Assistant Supervisor') return { bg: isDark ? 'rgba(251,191,36,0.15)' : '#FEF3C7', color: isDark ? '#FBBF24' : '#92400E' };
    return { bg: T.cardBgAlt, color: T.textMuted };
  };

  return (
    <div style={{ background: T.cardBg, borderRadius: 14, border: `1px solid ${T.border}`, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.textSecondary }}>✏️ Manage Roster</span>
        {!adding && (
          <button onClick={() => setAdding(true)}
            style={{ fontSize: 12, fontWeight: 700, color: T.blue, background: isDark ? 'rgba(96,165,250,0.15)' : '#EFF6FF', border: `1px solid ${T.blue}30`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer' }}>
            + Add Staff
          </button>
        )}
      </div>

      {adding && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name"
            style={{ flex: 1, minWidth: 150, padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 13, background: T.cardBg, color: T.textPrimary }} />
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1.5px solid ${T.border}`, fontSize: 13, background: T.cardBg, color: T.textPrimary }}>
            <option value="Staff">Staff</option>
            <option value="Assistant Supervisor">Asst. Supervisor</option>
            <option value="Supervisor">Supervisor</option>
          </select>
          <button onClick={addStaff} disabled={busy}
            style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: C.success, color: C.white, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Save</button>
          <button onClick={() => { setAdding(false); setName(''); }}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.cardBg, color: T.textMuted, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        </div>
      )}

      <div style={{ display: 'grid', gap: 4 }}>
        {roster.map(person => (
          <div key={person.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '7px 10px', borderRadius: 8, background: T.cardBgAlt, border: `1px solid ${T.divider}`,
          }}>
            {editingId === person.id ? (
              <div style={{ display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap' }}>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  style={{ flex: 1, minWidth: 120, padding: '5px 8px', borderRadius: 6, border: `1.5px solid ${T.border}`, fontSize: 13, background: T.cardBg, color: T.textPrimary }} />
                <select value={editRole} onChange={e => setEditRole(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: `1.5px solid ${T.border}`, fontSize: 13, background: T.cardBg, color: T.textPrimary }}>
                  <option value="Staff">Staff</option>
                  <option value="Assistant Supervisor">Asst. Supervisor</option>
                  <option value="Supervisor">Supervisor</option>
                </select>
                <button onClick={() => saveEdit(person.id)} disabled={busy}
                  style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#34D399' : C.success, background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                <button onClick={() => setEditingId(null)}
                  style={{ fontSize: 12, color: T.textFaint, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textSecondary }}>{person.full_name}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 5,
                    background: roleStyle(person.role).bg, color: roleStyle(person.role).color,
                  }}>{person.role === 'Assistant Supervisor' ? 'Asst Sup' : person.role}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setEditingId(person.id); setEditName(person.full_name); setEditRole(person.role); }}
                    style={{ fontSize: 12, color: T.blue, background: 'none', border: 'none', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => makeDormant(person.id, person.full_name)}
                    style={{ fontSize: 12, color: T.warning, background: 'none', border: 'none', cursor: 'pointer' }}>Dormant</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={toggleDormant}
          style={{ fontSize: 12, fontWeight: 600, color: T.textFaint, background: 'none', border: 'none', cursor: 'pointer' }}>
          {showDormant ? '▲ Hide Dormant' : '▼ Show Dormant'}
        </button>
      </div>
      {showDormant && (
        <div style={{ marginTop: 6 }}>
          {dormantList.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textFaint, fontStyle: 'italic' }}>No dormant staff.</div>
          ) : (
            <div style={{ display: 'grid', gap: 3 }}>
              {dormantList.map(person => (
                <div key={person.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isDark ? 'rgba(251,191,36,0.12)' : '#FEF3C7', borderRadius: 8, padding: '5px 10px', border: `1px solid ${isDark ? 'rgba(251,191,36,0.35)' : '#FDE68A'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, color: T.textMuted }}>{person.full_name}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 5, background: isDark ? 'rgba(251,191,36,0.25)' : '#FDE68A', color: isDark ? '#FBBF24' : '#92400E' }}>Dormant</span>
                  </div>
                  <button onClick={() => reactivate(person.id)} disabled={busy}
                    style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#34D399' : C.success, background: 'none', border: 'none', cursor: 'pointer' }}>↺ Reactivate</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
